import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createCategory,
  deleteCategory,
  fetchCategory,
  updateCategory,
} from "./thunks";
import { Category } from "@/types";
import { toast } from "react-toastify";

interface PaginatedCategory {
  data: Category[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface CategoryState {
  categories: PaginatedCategory;
  loading: boolean;
  error: string | null;
  fetched: boolean;
}

const initialState: CategoryState = {
  categories: { data: [] },
  loading: false,
  error: null,
  fetched: false,
};

const Categorylice = createSlice({
  name: "Category",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.categories = { data: [] };
      state.loading = false;
      state.error = null;
      state.fetched = false;
    },
    resetFetched: (state) => {
      state.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategory.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.fetched = true;
        const payload = action.payload;
        if (Array.isArray(payload.data)) {
          // Dados não paginados
          state.categories = { data: payload.data };
        } else {
          // Dados paginados
          state.categories = {
            data: payload.data.data,
            total: payload.data.total,
            currentPage: payload.data.current_page,
            perPage: payload.data.per_page,
          };
        }
      })
      .addCase(fetchCategory.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        state.fetched = false;
      })
      // Create
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createCategory.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.categories.data.push(action.payload.data);
          toast.success("Categoria cadastrado com sucesso!");
        },
      )
      .addCase(createCategory.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error ||
            "Não foi possível cadastrar a categoria, tente novamente!",
        );
      })

      // Update
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateCategory.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.categories.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.categories.data[index] = action.payload.data;
          }
          toast.success("Categoria atualizada com sucesso!");
        },
      )
      .addCase(updateCategory.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error ||
            "Não foi possível atualizar a categoria, tente novamente!",
        );
      })

      // Delete
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteCategory.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.categories.data = state.categories.data.filter(
            (s) => String(s.id) != action.payload.data.id,
          );
          toast.success("Categoria excluída com sucesso!");
        },
      )
      .addCase(deleteCategory.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error ||
            "Não foi possível excluir a categoria, tente novamente!",
        );
      });
  },
});

export const { clearSuppliers, resetFetched } = Categorylice.actions;
export default Categorylice.reducer;
